/**
 * Calendar dates for timesheets / working hours use the employee's work region
 * (e.g. India → Asia/Kolkata), not the laptop OS date helpers alone.
 *
 * Resolution order: USER_DATA region/country → browser IANA zone → Asia/Kolkata.
 * Display timestamps use getDisplayTimeZone() the same way.
 */

const REGION_TIMEZONE = {
  IN: "Asia/Kolkata",
  INDIA: "Asia/Kolkata",
  US: "America/Los_Angeles",
  USA: "America/Los_Angeles",
  "UNITED STATES": "America/Los_Angeles",
};

const COUNTRY_NAME_TIMEZONE = {
  india: "Asia/Kolkata",
  "united states": "America/Los_Angeles",
  usa: "America/Los_Angeles",
  us: "America/Los_Angeles",
};

function readUserData() {
  try {
    return JSON.parse(localStorage.getItem("USER_DATA") || "{}");
  } catch {
    return {};
  }
}

function readCountryMaster() {
  try {
    return JSON.parse(localStorage.getItem("country_master") || "[]");
  } catch {
    return [];
  }
}

function timezoneFromCountryId(countryId) {
  if (countryId == null || countryId === "") return null;
  const countries = readCountryMaster();
  const match = countries.find(
    (c) =>
      String(c.country_id ?? c.id) === String(countryId) ||
      String(c.id) === String(countryId),
  );
  const name = String(
    match?.country || match?.value || match?.name || "",
  )
    .trim()
    .toLowerCase();
  return COUNTRY_NAME_TIMEZONE[name] || null;
}

/** Resolve timezone from USER_DATA region / country only (no defaults). */
export function resolveUserProfileTimeZone() {
  const user = readUserData();
  const regionKeys = [
    user.location_region,
    user.locationRegion,
    user.holiday_region,
    user.holidayRegion,
    user.location,
  ];
  for (const raw of regionKeys) {
    const code = String(raw || "")
      .trim()
      .toUpperCase();
    if (REGION_TIMEZONE[code]) return REGION_TIMEZONE[code];
  }

  const fromCountry =
    timezoneFromCountryId(user.country_id) ||
    timezoneFromCountryId(user.business_country_id);
  if (fromCountry) return fromCountry;

  return null;
}

/**
 * Browser IANA timezone from Intl (OS/browser setting).
 * Returns the full zone string when available.
 */
export function resolveBrowserTimeZone() {
  try {
    const tz = String(
      Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    ).trim();
    return tz || null;
  } catch {
    return null;
  }
}

/**
 * IANA timezone for working-hours / timesheet calendar day boundaries.
 * 1) Employee profile region/country (India → Asia/Kolkata, US → America/Los_Angeles)
 * 2) Browser/OS IANA zone
 * 3) Asia/Kolkata default
 *
 * Profile wins so India staff with a US system clock still use the India work day.
 */
export function getWorkCalendarTimeZone() {
  return (
    resolveUserProfileTimeZone() ||
    resolveBrowserTimeZone() ||
    "Asia/Kolkata"
  );
}

/**
 * IANA timezone for showing timestamps across the app.
 * Profile region/country first, then browser zone, else IST.
 */
export function getDisplayTimeZone() {
  return (
    resolveUserProfileTimeZone() ||
    resolveBrowserTimeZone() ||
    "Asia/Kolkata"
  );
}

function ymdFromParts(parts) {
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  if (!y || !m || !d) return "";
  return `${y}-${m}-${d}`;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Format an instant in the viewer's display timezone.
 * Output matches existing UI: "MMM DD yyyy" or "MMM DD yyyy HH:mm".
 */
export function formatInDisplayTimeZone(value, { includeTime = true } = {}) {
  if (value == null || value === "") return "";

  const s = String(value).trim();
  // Pure calendar dates must not shift by timezone.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    const monthLabel = SHORT_MONTHS[Number(m) - 1] || m;
    return includeTime
      ? `${monthLabel} ${d} ${y} 00:00`
      : `${monthLabel} ${d} ${y}`;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const tz = getDisplayTimeZone();
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "short",
      day: "2-digit",
      ...(includeTime
        ? {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            hourCycle: "h23",
          }
        : {}),
    }).formatToParts(date);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    const month = get("month");
    const day = get("day");
    const year = get("year");
    if (!month || !day || !year) return "";
    if (!includeTime) return `${month} ${day} ${year}`;

    let hour = get("hour");
    const minute = get("minute");
    // Some engines emit "24" for midnight with hour12:false — normalize.
    if (hour === "24") hour = "00";
    return `${month} ${day} ${year} ${pad2(hour)}:${pad2(minute)}`;
  } catch {
    return "";
  }
}

/** Time-only (HH:mm) in the display timezone. */
export function formatTimeInDisplayZone(value) {
  if (value == null || value === "") return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const tz = getDisplayTimeZone();
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      hourCycle: "h23",
    }).formatToParts(date);
    let hour = parts.find((p) => p.type === "hour")?.value || "";
    const minute = parts.find((p) => p.type === "minute")?.value || "";
    if (hour === "24") hour = "00";
    if (!hour || !minute) return "";
    return `${pad2(hour)}:${pad2(minute)}`;
  } catch {
    return "";
  }
}

/** Format a Date (from MUI picker) as YYYY-MM-DD in the work region. */
export function formatPickerDateYmd(date) {
  if (date == null || date === "") return "";
  if (typeof date === "string") {
    const iso = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  }
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  const tz = getWorkCalendarTimeZone();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  return ymdFromParts(parts);
}

/** Today's calendar date (YYYY-MM-DD) in the work region. */
export function getTodayYmdInWorkZone() {
  return formatPickerDateYmd(new Date());
}
